/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — logged-in customer self-service area:
 *              events/invoices/receipts tabs, self-reschedule/cancel booking,
 *              logout
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        tab
 *   key:       tab get/set (drives applyTab), loadEventTask, loadInvoiceTask,
 *              loadReceiptTask, checkHomeTask, cancelEventTask,
 *              rescheduleEventTask, logoutTask
 *   uses:      applicationController (@controller('application')); intl/
 *              notify/session/router/store/log services; beebee-base/lib/utility
 *              (beebeeAjaxGet/Post, notifyUserError), beebee-base/lib/get-config;
 *              moment-timezone
 *   siblings:  routes/user.js, templates/user.hbs
 *   tests:     controllers-logic-test.js, wave4-coverage-test.js,
 *              wave13-coverage-test.js, wave16-coverage-test.js,
 *              user-area-reads-test.js
 *   gotchas:   the `tab` query param is handled by a get/set pair on the
 *              controller (not `refreshModel` on the route) — refreshModel
 *              re-runs beforeModel/session bootstrap on every tab click, which
 *              dropped the whole app into the full-screen loading substate on
 *              a plain in-route transition.
 *
 * Customer self-service area (`/user`): tab-driven event/invoice/receipt
 * lists plus self-service reschedule/cancel, gated by tenant booking_settings
 * (allow_self_reschedule, allow_self_cancel, hour-notice thresholds).
 */
import Controller from '@ember/controller';
import moment from 'moment-timezone';
import { beebeeAjaxGet, beebeeAjaxPost, notifyUserError } from 'beebee-base/lib/utility';

import { computed, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
export default class UserController extends Controller {
  @iService intl;
  @iService notify;
  @iService session;
  @iService router;
  @iService store;
  @iService log;
  @iController('application') applicationController;

  queryParams = ['tab'];

  @tracked _tab = null;

  showTab = null;

  /**
   * The `tab` query param reacts here rather than through `refreshModel` on the
   * route: refreshing the model re-ran `beforeModel` — session bootstrap and
   * all — which dropped the app into the full-screen loading substate on every
   * menu click, so a plain in-route transition looked like a page reload.
   */
  get tab() {
    return this._tab;
  }

  set tab(value) {
    const next = value ?? null;

    if (next === this._tab) {
      return;
    }

    this._tab = next;
    this.applyTab(next);
  }

  /**
   * Opens the section requested through the `tab` query param, so the mobile
   * menu can link straight into a section from any route.
   */
  applyTab(tab) {
    if (tab === 'event') {
      this.loadEventTask.perform();
    } else if (tab === 'invoice') {
      this.loadInvoiceTask.perform();
    } else if (tab === 'receipt') {
      this.loadReceiptTask.perform();
    } else {
      set(this, 'showTab', null);
    }
  }

  // Self-management state
  selectedEventForReschedule = null;
  showCancelConfirm = null;

  @computed('session.option_field.booking_settings.allow_self_reschedule')
  get allowSelfReschedule() {
    return !!this.session.option_field?.booking_settings?.allow_self_reschedule;
  }

  @computed('session.option_field.booking_settings.allow_self_cancel')
  get allowSelfCancel() {
    return !!this.session.option_field?.booking_settings?.allow_self_cancel;
  }

  @computed('session.option_field.booking_settings.self_cancel_hours_notice')
  get cancelHoursNotice() {
    return this.session.option_field?.booking_settings?.self_cancel_hours_notice ?? 24;
  }

  @computed('session.option_field.booking_settings.self_reschedule_hours_notice')
  get rescheduleHoursNotice() {
    return this.session.option_field?.booking_settings?.self_reschedule_hours_notice ?? 24;
  }

  @computed('listEvents.[]')
  get upcomingEvents() {
    const now = new Date();
    return (this.listEvents || []).filter((e) => new Date(e.start_date) > now);
  }

  @computed('listEvents.[]')
  get pastEvents() {
    const now = new Date();
    return (this.listEvents || []).filter((e) => new Date(e.start_date) <= now);
  }

  @computed('upcomingEvents.[]', 'cancelHoursNotice', 'rescheduleHoursNotice')
  get upcomingEventsEnriched() {
    const now = moment();
    return this.upcomingEvents.map((e) => {
      const hoursUntil = moment(e.start_date).diff(now, 'hours');
      return {
        event: e,
        cancelDisabled: hoursUntil < this.cancelHoursNotice,
        rescheduleDisabled: hoursUntil < this.rescheduleHoursNotice,
      };
    });
  }

  @computed('session.option_field.booking_settings.hide_event_list')
  get hideEvent() {
    if (this.session.option_field?.booking_settings?.hide_event_list) {
      return this.session.option_field?.booking_settings?.hide_event_list;
    }

    return false;
  }

  @computed('session.option_field.booking_settings.hide_invoice_list')
  get hideInvoice() {
    if (this.session.option_field?.booking_settings?.hide_invoice_list) {
      return this.session.option_field?.booking_settings?.hide_invoice_list;
    }

    return false;
  }

  @computed('session.option_field.booking_settings.hide_receipt_list')
  get hideReceipt() {
    if (this.session.option_field?.booking_settings?.hide_receipt_list) {
      return this.session.option_field?.booking_settings?.hide_receipt_list;
    }

    return false;
  }

  @(task(function* () {
    yield timeout(250);

    yield this.session.me;
    if (!this.session.me?.id) {
      return;
    }

    const redirectPage = this.session.me.get('role.redirect_page');

    if (redirectPage && getConfig(this).environment !== 'development') {
      if (redirectPage.substring(0, 4) === 'http') {
        window.location.href = redirectPage;
      } else if (!document.URL.includes(redirectPage)) {
        window.location.href = `/${redirectPage}`;
      }
    }
  }).restartable())
  checkHomeTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'event');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('event', {});
      set(this, 'listEvents', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadEventTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'invoice');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('invoice', {
        document_type: 'invoice',
      });
      set(this, 'listInvoices', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadInvoiceTask;

  @(task(function* () {
    yield timeout(250);

    if (!this.session.me?.id) {
      return;
    }

    set(this, 'showTab', 'receipt');
    set(this, 'showMenu', false);

    try {
      let e = yield this.store.query('invoice', {
        document_type: 'receipt',
      });
      set(this, 'listReceipts', e);
      return e;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return false;
    }
  }).restartable())
  loadReceiptTask;

  /**
   * The portal's two self-service writes (cancel, reschedule) target routes no API
   * serves yet: `cancel_booking` and `reschedule_booking` have zero occurrences in
   * api_beebeeboard (feb18dd7) and in v4. Product confirmed both features are meant
   * to exist, so the buttons stay and the endpoints are being built — POP-125
   * (cancel) and POP-126 (reschedule).
   *
   * Until they ship, a missing route reads to the user as an unexplained failure on
   * a button the interface offered them. The two catches below say "not available
   * yet" for exactly that case. Presentation only — nothing is written either way —
   * and both branches must go when the endpoints land, or a genuine 404 from a live
   * route will be mislabelled as "coming soon".
   */
  _isMissingBookingRoute(e) {
    return e?.status === 404 || e?.status === 405 || e?.status === 501;
  }

  @(task(function* (event) {
    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/cancel_booking`, this.session.token, {
        event_id: event.id,
        contact_id: this.session.me?.id,
      });

      const updated = (this.listEvents || []).filter((e) => e.id !== event.id);
      set(this, 'listEvents', updated);
      set(this, 'showCancelConfirm', null);
      this.notify.success(this.intl.t('save done'));
    } catch (e) {
      // See _isMissingBookingRoute: POP-125 / POP-126 are not built yet.
      if (this._isMissingBookingRoute(e)) {
        this.notify.warning(this.intl.t('not available yet'));
      } else {
        notifyUserError(this.notify, e, this.intl);
      }
    }
  }).drop())
  cancelEventTask;

  @(task(function* (event, newSlot) {
    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/reschedule_booking`, this.session.token, {
        event_id: event.id,
        contact_id: this.session.me?.id,
        new_from_date: newSlot.start,
        new_to_date: newSlot.end,
        stanza_id: newSlot.stanza_id || null,
      });

      set(this, 'selectedEventForReschedule', null);
      this.loadEventTask.perform();
      this.notify.success(this.intl.t('save done'));
    } catch (e) {
      // See _isMissingBookingRoute: POP-125 / POP-126 are not built yet.
      if (this._isMissingBookingRoute(e)) {
        this.notify.warning(this.intl.t('not available yet'));
      } else {
        notifyUserError(this.notify, e, this.intl);
      }
    }
  }).drop())
  rescheduleEventTask;

  @(task(function* () {
    try {
      yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/../logout?access_token=${this.session.token}`);
    } catch (e) {
      // Server-side logout failing must not block the client-side logout below
      this.log.warn('logoutTask: server logout call failed', e);
    }

    if (this.socket) {
      this.socket.emit('logout', {
        user: this.content?.me?.id,
        token: this.session.token,
      });
    }

    this.session.logout();

    // Niente `store.unloadAll()` + `transitionTo('index')` + attesa prima del
    // reload: una navigazione non ferma JavaScript, quindi la transizione
    // in-app renderizzava davvero l'index sopra una cache appena svuotata e
    // ogni @attr / hasMany ancora a schermo risolveva su una entry mancante
    // per tutta la finestra. E' lo stesso teardown che beebee-base ha tolto da
    // finishLogoutRedirect dopo Sentry EMBER-MEDICAL-2H3, qui aggravato dai
    // 100ms di attesa esplicita. Un solo document load arriva all'index con
    // uno store vuoto per costruzione; `replace` non lascia inoltre l'URL
    // autenticato nella history.
    this._redirectToIndex();
  }).drop())
  logoutTask;

  /**
   * La navigazione vera, isolata perche' un test possa guidare il logout senza
   * portarsi via il runner (stesso motivo di `redirectToHost` in beebee-base).
   */
  /* istanbul ignore next -- real document navigation */
  _redirectToIndex() {
    window.location.replace(getConfig(this).rootURL);
  }
}
